<script lang="ts">
	import { getContext, onMount } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { Mode } from "#gen/bundles/v1/service_pb.js";
	import type { Domain, GetDomainPublicConfigResponse } from "#gen/links/v1/links_pb.js";
	import { FcColorBlack } from "#lib/code.js";
	import { buildFooterLinkList, isDomainDPACompliant } from "#lib/components/admin/cookiePolicy/utils.js";
	import { debounce } from "#lib/debounce.js";
	import { isLight, lightenDarkenColor } from "#lib/theme.js";
	import type { Readable, Writable } from "svelte/store";

	interface Props {
		backgroundColor: string;
		mode: Mode;
	}

	const { backgroundColor, mode }: Props = $props();
	const domain: Writable<Domain | undefined> = getContext("domain");
	const dpaDomainInfo: Readable<GetDomainPublicConfigResponse | undefined> = getContext("dpaDomainInfo");
	const debouncedUpdate = debounce(updatePrefixes, 100);
	let footerContainer: HTMLDivElement | null = $state(null);
	let hidePrefix = $state<boolean[]>([]);
	let resizeObserver: ResizeObserver | null = $state(null);

	onMount(() => {
		if (footerContainer && typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(debouncedUpdate);
			resizeObserver.observe(footerContainer);
		}
		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	});

	const reconciledDomain = $derived.by(() => {
		if ($dpaDomainInfo) {
			return {
				domain: $dpaDomainInfo.domain,
				oneTrustId: $dpaDomainInfo.oneTrustId,
				privacyPolicyLinkHash: $dpaDomainInfo.privacyPolicyLinkHash,
				cookiePreferencesLinkLabel: $dpaDomainInfo.cookiePreferencesLinkLabel,
				doNotSellPolicyLinkLabel: $dpaDomainInfo.doNotSellPolicyLinkLabel,
				doNotSellPolicyLinkUrl: $dpaDomainInfo.doNotSellPolicyLinkUrl,
				privacyPreferencesLabel: $dpaDomainInfo.privacyPreferencesLabel,
				privacyPreferencesUrl: $dpaDomainInfo.privacyPreferencesUrl,
				privacySmileUrl: $dpaDomainInfo.privacySmileUrl,
				privacyPolicyLinkLabel: $dpaDomainInfo.privacyPolicyLinkLabel,
				privacyLinks: $dpaDomainInfo.privacyLinks,
			};
		}
		return $domain;
	});

	const footerLinks = $derived(reconciledDomain ? buildFooterLinkList(reconciledDomain) : []);

	function updatePrefixes() {
		const linkEls = footerContainer ? Array.from(footerContainer.querySelectorAll<HTMLDivElement>("[data-footer-link]")) : [];
		hidePrefix = Array.from({ length: linkEls.length }, () => false);
		let lastRowSeenTop: number | null = null;
		for (let i = 0; i < linkEls.length; i++) {
			const el = linkEls[i];
			if (el) {
				const rect = el.getBoundingClientRect();
				if (lastRowSeenTop === null || rect.top > lastRowSeenTop) {
					hidePrefix[i] = true;
					lastRowSeenTop = rect.top;
				} else {
					hidePrefix[i] = false;
				}
			}
		}
	}

	function handleCookieSettings() {
		window?.OneTrust?.ToggleInfoDisplay();
	}
</script>

{#if isDomainDPACompliant(reconciledDomain)}
	<div
		bind:this={footerContainer}
		class={twMerge("flex w-full flex-wrap items-center justify-center px-4 py-1.5 text-xs font-normal", isLight(backgroundColor) ? "text-gray-900" : "text-white", mode === Mode.EDITING && "rounded-b-3xl", backgroundColor === FcColorBlack && "bg-gray-800")}
		style={backgroundColor !== FcColorBlack ? `background-color: ${lightenDarkenColor(backgroundColor, -25.5)}` : undefined}
	>
		{#each footerLinks as link, index (link.id)}
			<div data-footer-link class="inline-flex items-center whitespace-nowrap">
				{#if index > 0 && !hidePrefix[index]}
					<span aria-hidden="true" class="h-4 border-l border-current"></span>
				{/if}
				{#if link.element === "a"}
					<a href={link.href} rel="external" class={twMerge("mx-2 py-2.5", mode === Mode.EDITING && "pointer-events-none")}>
						{link.label}
					</a>
				{:else if link.element === "button"}
					<button type="button" onclick={handleCookieSettings} id={link.buttonId} class={twMerge("mx-2 px-0 py-2.5 text-xs whitespace-nowrap hover:cursor-pointer")}>
						{link.label}
					</button>
				{/if}
			</div>
		{/each}
	</div>
{/if}
